import {
  ECampaignKeys,
  TCampaignAgentJunction,
} from '~/features/campaigns/models'
import {
  getCampaignJunctions,
  isSharedCampaign,
} from '~/features/campaigns/utils/campaignJunctions'

type TCampaignLike = Record<string, unknown> | null | undefined

export const getCampaignAssistantJunctions = (
  campaign: TCampaignLike,
): TCampaignAgentJunction[] => getCampaignJunctions(campaign)

export const getCampaignPrimaryAssistantJunction = (
  campaign: TCampaignLike,
): TCampaignAgentJunction | null => {
  const campaignAgentId = String(campaign?.[ECampaignKeys.agentId] || '')
  const campaignJunctions = getCampaignAssistantJunctions(campaign)

  if (campaignAgentId) {
    const matchingJunction = campaignJunctions.find(
      (junction) =>
        String(junction.agentId || junction.Agent?.id || '') ===
        campaignAgentId,
    )
    if (matchingJunction) {
      return matchingJunction
    }
  }

  return (
    campaignJunctions.find((junction) =>
      Boolean(junction.agentId || junction.Agent?.id),
    ) || null
  )
}

export const getCampaignPrimaryAssistantId = (
  campaign: TCampaignLike,
): string => {
  const campaignAgentId = String(campaign?.[ECampaignKeys.agentId] || '')
  const primaryJunction = getCampaignPrimaryAssistantJunction(campaign)

  return String(
    primaryJunction?.agentId ||
      primaryJunction?.Agent?.id ||
      campaignAgentId ||
      '',
  )
}

export const getCampaignJunctionAssistantIds = (
  campaign: TCampaignLike,
): string[] =>
  Array.from(
    new Set(
      getCampaignAssistantJunctions(campaign)
        .map((junction) => String(junction.agentId || junction.Agent?.id || ''))
        .filter(Boolean),
    ),
  )

export const getDefaultSharedCampaignAssistantIds = (
  campaign: TCampaignLike,
): string[] => {
  const campaignAgentId = String(campaign?.[ECampaignKeys.agentId] || '')
  const junctionAssistantIds = getCampaignJunctionAssistantIds(campaign)

  return Array.from(
    new Set([...junctionAssistantIds, campaignAgentId].filter(Boolean)),
  )
}

export const getInitialSharedCampaignAssistantIds = (
  campaign: TCampaignLike,
): string[] => {
  const sharedAgentIdsFromApi =
    (campaign?.[ECampaignKeys.sharedCampaignAgentIds] as
      | string[]
      | undefined) || []
  const defaults = getDefaultSharedCampaignAssistantIds(campaign)
  const isShared = isSharedCampaign(campaign)

  if (sharedAgentIdsFromApi.length) {
    return Array.from(
      new Set([...sharedAgentIdsFromApi.map(String), ...defaults]),
    )
  }

  return isShared ? defaults : []
}
